import { httpCreateWorkspace } from 'services/backofficeIntegration/http/endpoints/workspace/httpCreateWorkspace';
import { httpGetWorkspaceMembersOfCustomer } from 'services/backofficeIntegration/http/endpoints/workspace/httpGetWorkspaceMembersOfCustomer';
import { assertNonNullable } from 'utils/typescript/nonNullable';

/**
 * Extracted logic for getting workspace ID from workspace members.
 * This function can be used both in React Query hooks and Redux sagas.
 */
export const getWorkspaceId = async (
  customerId: number,
  currentSelectedWorkspaceId: string | null
): Promise<string | null> => {
  assertNonNullable(customerId);

  // This is a list (a workspace member entity) for every workspace the user is a member of
  const myWorkspaceMembershipEntries = await httpGetWorkspaceMembersOfCustomer.callEndpoint({
    customerId,
  });

  // If customer doesn't have a workspace, create one and use it
  if (myWorkspaceMembershipEntries.length === 0) {
    const createdWorkspace = await httpCreateWorkspace.callEndpoint();
    return createdWorkspace.id;
  }

  // Validate selected workspace id against the list of workspaces the customer is a member of
  if (currentSelectedWorkspaceId) {
    const foundWorkspace = myWorkspaceMembershipEntries.find(
      member => member.workspaceId === currentSelectedWorkspaceId
    );
    if (foundWorkspace) {
      return foundWorkspace.workspaceId;
    }
  }

  // If there's only one, use it
  if (myWorkspaceMembershipEntries.length === 1) {
    return myWorkspaceMembershipEntries[0].workspaceId || null;
  }

  // In case of multiple workspaces, use the first there the customer is NOT the owner
  const firstParticipatingWorkspace = myWorkspaceMembershipEntries.find(
    member => member.role !== 'OWNER'
  );
  if (firstParticipatingWorkspace) {
    return firstParticipatingWorkspace.workspaceId;
  }

  // Fallback to the first workspace
  return myWorkspaceMembershipEntries[0].workspaceId || null;
};
